/**
 * Foundation/Core — Desktop Notification API wrapper
 *
 * Wrapper agnostico intorno alla `Notification` API del browser (RFC W3C).
 * Nessun riferimento a Product Pack: titolo, icona, categorization sono
 * completamente iniettati dal chiamante.
 *
 * PROP di `fireNotification`:
 * - title      (obbligatoria): stringa del titolo
 * - body       (opzionale): testo del messaggio
 * - tag        (opzionale): tag di deduplica lato SO
 * - onClick    (opzionale): callback al click (default: focus finestra)
 * - icon       (opzionale): path icona (default: nessuna)
 * - badge      (opzionale): path badge (default: nessuna)
 * - autoCloseMs (opzionale): timeout auto-close (default: 12000, 0=disabilita)
 *
 * FACTORY di persistenza on/off:
 * `createLocalStorageToggle(storageKey)` restituisce `{ getEnabled, setEnabled }`
 * — utile ai Product Pack per persistere il proprio flag "opt-in" senza colonizzare
 * lo storage con prefissi Foundation.
 */

const noop = () => {};

/** True se il browser supporta la Notification API. */
export function isDesktopNotificationSupported() {
  return typeof window !== "undefined" && "Notification" in window;
}

/** Ritorna lo stato del permesso: "default" | "granted" | "denied" | "unsupported". */
export function getDesktopPermission() {
  if (!isDesktopNotificationSupported()) return "unsupported";
  return Notification.permission;
}

/** Richiede il permesso Notification (Promise + fallback callback per Safari legacy). */
export async function requestDesktopPermission() {
  if (!isDesktopNotificationSupported()) return "unsupported";
  if (Notification.permission === "granted") return "granted";
  if (Notification.permission === "denied") return "denied";
  try {
    const p = await Notification.requestPermission();
    return p;
  } catch (_e) {
    return await new Promise((resolve) => {
      try {
        Notification.requestPermission((r) => resolve(r || "default"));
      } catch (_ee) { resolve("default"); }
    });
  }
}

/**
 * Emette una notifica desktop nativa. Ritorna true se effettivamente creata.
 * Il chiamante e' responsabile della logica di gating (opt-in, category filter):
 * questo wrapper non consulta localStorage ne' categorie.
 */
export function fireNotification({ title, body, tag, onClick, icon, badge, autoCloseMs = 12_000 }) {
  if (!isDesktopNotificationSupported()) return false;
  if (Notification.permission !== "granted") return false;
  if (!title) return false;
  try {
    const options = {
      body: body || "",
      renotify: true,
      requireInteraction: false,
      silent: false,
    };
    if (tag) options.tag = tag;
    if (icon) options.icon = icon;
    if (badge) options.badge = badge;
    const n = new Notification(title, options);
    n.onclick = (e) => {
      e.preventDefault();
      try { window.focus(); } catch (_e) { noop(); }
      try { if (onClick) onClick(); } catch (_e) { noop(); }
      try { n.close(); } catch (_e) { noop(); }
    };
    if (autoCloseMs > 0) {
      setTimeout(() => { try { n.close(); } catch (_e) { noop(); } }, autoCloseMs);
    }
    return true;
  } catch (_e) {
    return false;
  }
}

/**
 * Factory generica di toggle localStorage. Il Product Pack decide la chiave
 * (es. `"presenze_desktop_notifications_enabled"`, `"clienti_desktop_notifications_enabled"`).
 * Il Foundation NON impone alcun prefisso.
 */
export function createLocalStorageToggle(storageKey) {
  if (typeof storageKey !== "string" || !storageKey) {
    throw new Error("createLocalStorageToggle: storageKey obbligatorio");
  }
  return {
    getEnabled() {
      try { return localStorage.getItem(storageKey) === "1"; }
      catch (_e) { return false; }
    },
    setEnabled(enabled) {
      try { localStorage.setItem(storageKey, enabled ? "1" : "0"); }
      catch (_e) { noop(); }
    },
  };
}
